import { Component } from '@angular/core';
import { FieldConfigurationService } from '../../core/service/field-configuration.service';
import { GridViewManager } from '../../core/model/grid-view-manager';
import { NGXLogger } from 'ngx-logger';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { FieldConfigurationEdit } from '../model/fieldConfigurationEdit';
import { Subject } from 'rxjs';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';

@Component({
  selector: 'app-field-configuration-list',
  templateUrl: './field-configuration-list.component.html',
  styleUrls: ['./field-configuration-list.component.scss']
})
export class FieldConfigurationListComponent
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public testingDialogOpened: Subject<boolean> = new Subject();
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: FieldConfigurationEdit;
  public selectedFieldConfiguration: FieldConfigurationEdit;
  public filterText = '';

  constructor(private fieldConfigurationService: FieldConfigurationService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    const fieldConfigurationId = this.selectedFieldConfiguration.id;
    if (confirm)
    {
      // Proceed with the deletion
      this.fieldConfigurationService.deleteFieldConfiguration(fieldConfigurationId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== fieldConfigurationId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('FIELD-CONFIGURATION-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedFieldConfiguration = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.fieldConfigurationService.existsFieldConfiguration(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('FIELD-CONFIGURATION-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.fieldConfigurationService.getFieldConfigurationById(id).subscribe({
            next: (data) =>
            {
              this.selectedFieldConfiguration = data as FieldConfigurationEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new FieldConfigurationEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.fieldConfigurationService.getFieldConfigurationById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(fieldConfiguration: FieldConfigurationEdit): void
  {
    this.fieldConfigurationService.saveFieldConfiguration(fieldConfiguration.id, fieldConfiguration, this.isNew).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedDate into Date object
        data.lastModifiedDate = new Date(data.lastModifiedDate);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.logger.info('saved', fieldConfiguration);

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('FIELD-CONFIGURATION-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  public openRuleTesting(): void
  {
    this.testingDialogOpened.next(true);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.fieldConfigurationService.getFieldConfigurationsList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedDate = new Date(d.lastModifiedDate); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
